import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { confirmBrand } from "@/lib/api";
import { fmtMoney, COLORS } from "@/lib/format";
import { ScoreRing } from "@/components/ScoreRing";
import { CoverageMeter } from "@/components/CoverageMeter";
import { ConfidencePill } from "@/components/Pills";
import { Eyebrow, TopBar, Footer, CtaBand } from "@/components/SiteChrome";
import { LockedSectionsList } from "@/components/evidence/EvidenceCard";
import { WastedSpendSection, HarvestSection, MatchTypeSection } from "@/components/evidence/CoreSections";
import { AdMixSection, PlacementSection, PortfolioSection } from "@/components/evidence/GrowthSections";
import { ConvertingTermsSection } from "@/components/evidence/ConvertingTermsSection";
import { FindingsSection } from "@/components/evidence/FindingsSection";

// Bulk-file product only renders the sections the bulk file can score.
// Budget, organics, sqp and repeatPurchase are always null and surfaced in `notAvailableFromBulk`.
const SECTION_ORDER = ["wastedSpend", "convertingTerms", "harvest", "matchType", "adMix", "placement", "portfolio"];
const SECTION_TITLES = {
  wastedSpend: "Wasted spend",
  convertingTerms: "Converting search terms",
  harvest: "Harvest: proven winners",
  matchType: "Match types",
  adMix: "Ad type mix",
  placement: "Placement efficiency",
  portfolio: "Products",
  budget: "Out-of-budget missed sales",
  organics: "TACoS & organic share",
  sqp: "Category visibility",
  repeatPurchase: "Retention",
};

const NOT_AVAILABLE_LABELS = {
  budget: "Out-of-budget missed sales",
  organics: "TACoS & organic share",
  repeatPurchase: "Retention",
  sqp: "Category visibility",
};

// Badge for an evidence section = sum of its non-low scorecard lines.
const sectionBadge = (audit, id) => {
  const lines = (audit.scorecard.lines || []).filter((l) => l.section === id);
  if (!lines.length) return {};
  const low = lines.every((l) => l.confidence === "low");
  const cost = lines.filter((l) => l.type === "cost_saving").reduce((s, l) => s + l.monthly, 0);
  const sales = lines.filter((l) => l.type === "sales_increase").reduce((s, l) => s + l.monthly, 0);
  const cur = audit.meta.currency;
  if (cost > 0) return { badge: `−${fmtMoney(cur, cost)}/mo`, color: low ? COLORS.greyD : COLORS.good, footnote: lines[0].assumption };
  if (sales > 0) return { badge: `+${fmtMoney(cur, sales)}/mo`, color: low ? COLORS.greyD : COLORS.ink, footnote: lines[0].assumption };
  return { footnote: lines[0].assumption };
};

const BrandBanner = ({ audit, setAudit }) => {
  const brand = audit.meta.inferredBrand;
  const [dismissed, setDismissed] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [value, setValue] = useState(brand?.value || "");
  const [busy, setBusy] = useState(false);

  if (!audit.meta.needsBrandConfirmation || dismissed || !brand) return null;

  const post = async (payload) => {
    setBusy(true);
    try {
      const res = await confirmBrand(audit.auditId, payload);
      setAudit(res.audit);
      // Response now has needsBrandConfirmation=false, so banner hides automatically.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b-2 border-[color:var(--ink)] bg-[color:var(--warn)]/15" data-testid="brand-banner">
      <div className="mx-auto max-w-6xl px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-baseline gap-3 flex-1 min-w-0">
          <span className="ss-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--ink)] shrink-0">
            Check brand
          </span>
          {!correcting ? (
            <p className="text-sm text-[color:var(--ink)] leading-snug">
              We treated{" "}
              <span className="font-semibold" data-testid="brand-banner-inferred">{brand.value}</span>{" "}
              as your brand name. Is that right?
            </p>
          ) : (
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Your brand name"
              className="ss-input flex-1 max-w-xs !py-2"
              autoFocus
              data-testid="brand-banner-input"
            />
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {busy ? (
            <Loader2 size={16} className="animate-spin text-[color:var(--green)]" />
          ) : !correcting ? (
            <>
              <button
                onClick={() => post({ confirm: true })}
                className="ss-btn-secondary !py-2 !px-4 text-xs"
                data-testid="brand-banner-yes"
              >
                <Check size={14} /> Yes, that's right
              </button>
              <button
                onClick={() => setCorrecting(true)}
                className="ss-btn-secondary !py-2 !px-4 text-xs"
                data-testid="brand-banner-no"
              >
                No — it's…
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="text-[color:var(--grey)] p-1"
                aria-label="Dismiss"
                data-testid="brand-banner-dismiss"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => value.trim() && post({ brand: value.trim() })}
                className="ss-btn-primary !py-2 !px-4 text-xs"
                data-testid="brand-banner-save"
              >
                Save
              </button>
              <button
                onClick={() => setCorrecting(false)}
                className="ss-btn-secondary !py-2 !px-4 text-xs"
                data-testid="brand-banner-cancel"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const LineRows = ({ lines, cur, sign, color }) => (
  <div className="divide-y divide-[color:var(--hairline)]">
    {lines.map((l, i) => (
      <a
        key={i}
        href={`#sec-${l.section}`}
        className="flex items-start justify-between gap-4 py-4 group no-underline"
        data-testid={`scorecard-line-${l.section}-${i}`}
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[color:var(--ink)] group-hover:text-[color:var(--green)] transition-colors flex items-center gap-2 flex-wrap">
            {l.label} <ConfidencePill level={l.confidence} />
          </p>
          <p className="text-xs text-[color:var(--grey)] mt-1 leading-relaxed">{l.assumption}</p>
        </div>
        <span className="shrink-0 ss-mono text-base font-semibold" style={{ color }}>
          {sign}{fmtMoney(cur, l.monthly)}
        </span>
      </a>
    ))}
  </div>
);

export default function ResultsView({ audit, setAudit, brandName }) {
  const cur = audit.meta.currency;
  const sc = audit.scorecard;
  const [margin, setMargin] = useState(sc.marginAssumptionPct || 30);
  const liveProfit = sc.monthlySavings + sc.monthlySalesIncrease * (margin / 100);
  const displayBrand = brandName || audit.meta.inferredBrand?.value || "Your brand";

  const costLines = sc.lines.filter((l) => l.type === "cost_saving" && l.confidence !== "low");
  const salesLines = sc.lines.filter((l) => l.type === "sales_increase" && l.confidence !== "low");
  const lowLines = sc.lines.filter((l) => l.confidence === "low");
  const lockedSections = audit.coverage?.lockedSections || [];
  const notAvailable = audit.meta?.notAvailableFromBulk || [];

  const renderSection = (id) => {
    const data = audit.sections?.[id];
    if (!data) return null;
    const { badge, color, footnote } = sectionBadge(audit, id);
    const common = { data, cur, badge, footnote };
    switch (id) {
      case "wastedSpend": return <WastedSpendSection key={id} {...common} badgeColor={color} />;
      case "convertingTerms": return <ConvertingTermsSection key={id} {...common} />;
      case "harvest": return <HarvestSection key={id} {...common} />;
      case "matchType": return <MatchTypeSection key={id} data={data} cur={cur} />;
      case "adMix": return (data.missing && data.missing.length === Object.keys(data.types || {}).length) ? null : <AdMixSection key={id} {...common} />;
      case "placement": return <PlacementSection key={id} {...common} />;
      case "portfolio": return <PortfolioSection key={id} {...common} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="results-view">
      <TopBar />
      <BrandBanner audit={audit} setAudit={setAudit} />

      {/* 1. Header band */}
      <div className="border-b-2 border-[color:var(--ink)] bg-[color:var(--panel)]">
        <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <Eyebrow className="mb-3">Profit scorecard</Eyebrow>
            <h1 className="font-display text-2xl sm:text-4xl text-[color:var(--ink)] leading-none" data-testid="results-header">
              {displayBrand}
              {audit.meta.marketplace ? <> · Amazon {audit.meta.marketplace}</> : null}
            </h1>
          </div>
          <div className="flex items-center gap-8">
            <div className="w-44">
              <CoverageMeter pct={audit.coverage.pct} />
              {notAvailable.length > 0 && (
                <p className="text-[10px] text-[color:var(--grey)] mt-2 leading-snug" data-testid="not-available-from-bulk">
                  Not from bulk file: {notAvailable.map((k) => NOT_AVAILABLE_LABELS[k] || k).join(", ")}
                </p>
              )}
            </div>
            <ScoreRing value={audit.score.value} size={110} />
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-6">
        {/* 2. Scorecard hero */}
        <section className="py-14 sm:py-20" data-testid="scorecard-hero">
          <Eyebrow className="mb-6 anim-fade-up anim-d1">What we found</Eyebrow>
          <p
            className="font-display leading-[0.9] text-6xl sm:text-8xl text-[color:var(--green)] anim-fade-up anim-d2 tabular-nums"
            data-testid="giant-money-figure"
          >
            {fmtMoney(cur, liveProfit)}
          </p>
          <p className="mt-4 ss-mono text-sm uppercase tracking-[0.14em] text-[color:var(--ink)]">
            /month profit impact · <span className="text-[color:var(--grey)]">that's {fmtMoney(cur, liveProfit * 12)} a year</span>
          </p>

          <div className="mt-10 border-t-2 border-[color:var(--ink)] grid grid-cols-1 md:grid-cols-3 divide-x divide-[color:var(--hairline)]">
            <div className="p-6" data-testid="tile-cost-reduction">
              <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--grey)] mb-3">
                Ad cost reduction
              </p>
              <p className="ss-mono text-3xl sm:text-4xl font-semibold text-[color:var(--good)] tabular-nums">
                {fmtMoney(cur, sc.monthlySavings)}<span className="text-base text-[color:var(--grey)]">/mo</span>
              </p>
              <p className="text-xs text-[color:var(--grey)] mt-3">Goes straight to profit</p>
            </div>
            <div className="p-6" data-testid="tile-sales-increase">
              <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--grey)] mb-3">
                Sales increase
              </p>
              <p className="ss-mono text-3xl sm:text-4xl font-semibold text-[color:var(--ink)] tabular-nums">
                {fmtMoney(cur, sc.monthlySalesIncrease)}<span className="text-base text-[color:var(--grey)]">/mo</span>
              </p>
              <p className="text-xs text-[color:var(--grey)] mt-3">
                = <span className="ss-mono">{fmtMoney(cur, sc.monthlySalesIncrease * (margin / 100))}</span>/mo profit at your margin
              </p>
            </div>
            <div className="p-6" data-testid="tile-directional-upside">
              <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--grey)] mb-3">
                Directional upside
              </p>
              <p className="ss-mono text-3xl sm:text-4xl font-semibold text-[color:var(--grey)] tabular-nums">
                {fmtMoney(cur, sc.directionalUpside)}<span className="text-base text-[color:var(--grey)]">/mo</span>
              </p>
              <p className="text-xs text-[color:var(--grey)] mt-3">Not counted in headline</p>
            </div>
          </div>

          {/* Margin slider */}
          <div className="mt-8 max-w-md ss-panel p-6" data-testid="margin-slider-card">
            <div className="flex items-baseline justify-between">
              <label className="ss-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--ink)]">
                Your gross margin
              </label>
              <span className="ss-mono text-sm font-semibold text-[color:var(--ink)]" data-testid="margin-value">{margin}%</span>
            </div>
            <input
              type="range"
              min={10}
              max={60}
              step={1}
              value={margin}
              onChange={(e) => setMargin(Number(e.target.value))}
              className="margin-slider w-full mt-4"
              data-testid="margin-slider"
            />
            <p className="ss-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--grey)] mt-3">
              Profit = ad savings + sales increase × margin. Drag to match your true gross margin.
            </p>
          </div>
        </section>
      </main>
      <CtaBand cur={cur} value={liveProfit} testId="cta-band-top" />
      <main className="mx-auto max-w-6xl px-6">
        {/* 3. Scorecard lines */}
        <section className="py-14 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="scorecard-lines">
          <div className="ss-panel p-6 sm:p-8">
            <p className="ss-kicker mb-3">Cost reductions</p>
            <h2 className="font-display text-2xl text-[color:var(--ink)]">Monthly ad spend saved</h2>
            <div className="mt-4">
              <LineRows lines={costLines} cur={cur} sign="−" color={COLORS.good} />
            </div>
            {sc.overlapDiscountApplied > 0 && (
              <div className="flex items-center justify-between pt-4 border-t border-[color:var(--hairline)] text-sm" data-testid="overlap-discount-row">
                <span className="text-[color:var(--grey)]">Overlapping levers discounted by {sc.overlapDiscountApplied}%</span>
                <span className="ss-mono font-semibold text-[color:var(--grey)]">−{sc.overlapDiscountApplied}%</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-4 mt-1 border-t-2 border-[color:var(--ink)] text-sm">
              <span className="ss-mono font-semibold uppercase tracking-[0.08em] text-[color:var(--ink)]">Total monthly savings</span>
              <span className="ss-mono font-semibold text-base" style={{ color: COLORS.good }}>−{fmtMoney(cur, sc.monthlySavings)}</span>
            </div>
          </div>
          <div className="ss-panel p-6 sm:p-8">
            <p className="ss-kicker mb-3">Sales growth</p>
            <h2 className="font-display text-2xl text-[color:var(--ink)]">Monthly incremental revenue</h2>
            <div className="mt-4">
              <LineRows lines={salesLines} cur={cur} sign="+" color={COLORS.ink} />
            </div>
            <div className="flex items-center justify-between pt-4 border-t-2 border-[color:var(--ink)] text-sm mt-1">
              <span className="ss-mono font-semibold uppercase tracking-[0.08em] text-[color:var(--ink)]">Total monthly sales increase</span>
              <span className="ss-mono font-semibold text-base text-[color:var(--ink)]">+{fmtMoney(cur, sc.monthlySalesIncrease)}</span>
            </div>
          </div>
        </section>

        {lowLines.length > 0 && (
          <section className="mb-10 border-2 border-dashed border-[color:var(--ink)]/40 bg-[color:var(--panel)] p-6 sm:p-8" data-testid="directional-upside-card">
            <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--grey)] mb-2">
              Directional upside — not in the headline
            </p>
            <LineRows lines={lowLines} cur={cur} sign="+" color={COLORS.greyD} />
          </section>
        )}

        {(sc.enablers || []).length > 0 && (
          <section className="mb-14 ss-panel p-6 sm:p-8" data-testid="enablers-card">
            <h2 className="font-display text-xl text-[color:var(--ink)]">Also worth doing</h2>
            <ul className="mt-4 space-y-3">
              {sc.enablers.map((e) => (
                <li key={e.section} className="text-sm text-[color:var(--grey)] leading-relaxed">
                  <span className="font-semibold text-[color:var(--ink)]">{SECTION_TITLES[e.section] || e.section}: </span>
                  {e.note}
                </li>
              ))}
            </ul>
            {sc.note && <p className="mt-4 pt-3 border-t border-[color:var(--hairline)] text-xs text-[color:var(--grey)] leading-relaxed">{sc.note}</p>}
          </section>
        )}

        {/* Findings */}
        <FindingsSection findings={audit.findings} cur={cur} />

        {/* 4. Evidence sections */}
        <section className="pb-16">
          <div className="mb-10">
            <Eyebrow className="mb-4">The receipts</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl text-[color:var(--ink)]">
              Where these numbers come from
            </h2>
          </div>
          <div className="space-y-8">{SECTION_ORDER.map(renderSection).filter(Boolean)}</div>
        </section>

        {lockedSections.length > 0 && (
          <section className="pb-16">
            <LockedSectionsList items={lockedSections} titles={SECTION_TITLES} />
          </section>
        )}
      </main>

      <CtaBand cur={cur} value={liveProfit} testId="cta-band-bottom" />
      <Footer />
    </div>
  );
}
